import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOrganizationProjectsRequest,
  fetchOrganizationRolesRequest,
  fetchPermissionsRequest,
} from 'modules/organization/actions';
import {
  selectCombinedRoles,
  selectOrganizationProjects,
  selectOrganizationPermissionsForForm,
} from 'modules/organization/selectors';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';
import { FormField } from 'ui/formField';
import { JsonView } from 'ui/treeView';
import { BasicSelect } from 'ui/selects';
import { spacing, Body1Bold } from 'ui/theme';
import { Role, ProjectRolePayload } from '../types';
import { Description } from './AuthenticationPolicyPage.styled';
import { CheckboxFormField } from './CheckboxFormField';
import { CopyToClipboardButton } from './CopyToClipboardButton';

const TEXT: Record<string, string> = {
  ORG_PERMISSIONS_TITLE: 'Organization permissions',
  PROJ_PERMISSIONS_TITLE: 'Project permissions',
  'Create projects': 'Add new projects for your organization.',
  'Manage members': 'Add, modify, and revoke access for members of your organization.',
  'Manage roles': 'Add and modify project permission sets that apply to your members.',
  'Manage billing': 'View overall account usage and update payment information.',
};

const SectionTitle = styled(Body1Bold)`
  margin-bottom: ${spacing.plusHalf};
`;

export const NONE = 'None';

export const SamlJsonBuilder = () => {
  const dispatch = useDispatch();
  const [selectedOrgPermissions, setSelectedOrgPermissions] = useState<string[]>([]);
  const [projectRoles, setProjectRoles] = useState<ProjectRolePayload[]>([]);

  useEffect(() => {
    dispatch(fetchPermissionsRequest());
    dispatch(fetchOrganizationProjectsRequest());
    dispatch(fetchOrganizationRolesRequest());
  }, []);

  const orgPermissions = useSelector(selectOrganizationPermissionsForForm);
  const orgProjects = useSelector(selectOrganizationProjects);
  const combinedRoles = useSelector(selectCombinedRoles);

  const roleOptions = [
    ...combinedRoles.map((role: Role) => ({
      id: role.name,
      label: role.name,
      value: role.name,
    })),
    { id: NONE, label: NONE, value: NONE },
  ];

  const onCheck = (orgPermission: string) => () => {
    const next = selectedOrgPermissions.includes(orgPermission)
      ? selectedOrgPermissions.filter((value) => value !== orgPermission)
      : [...selectedOrgPermissions, orgPermission];
    setSelectedOrgPermissions(next);
  };

  const onSelectProjectRole = (projectRole: string, projectId: number) => {
    let next = projectRoles.filter((role) => role.projectId !== projectId);
    if (projectRole !== NONE) next = [...next, { role: projectRole, projectId }];
    setProjectRoles(next);
  };

  const checkboxStatus = (orgPermission: string) =>
    selectedOrgPermissions.includes(orgPermission)
      ? CHECKBOX_STATUS.CHECKED
      : CHECKBOX_STATUS.UNCHECKED;

  const jsonSrc = {
    orgPermissions: selectedOrgPermissions,
    projectRoles,
  };

  return (
    <div>
      <SectionTitle>{TEXT.ORG_PERMISSIONS_TITLE}</SectionTitle>
      {orgPermissions.map(({ name }) => (
        <CheckboxFormField
          key={name}
          name={name}
          label={name}
          onClick={onCheck(name)}
          description={TEXT[name] && <Description>{TEXT[name]}</Description>}
        >
          <Checkbox name={name} onClick={onCheck(name)} status={checkboxStatus(name)} />
        </CheckboxFormField>
      ))}

      <SectionTitle>{TEXT.PROJ_PERMISSIONS_TITLE}</SectionTitle>
      {orgProjects.map((orgProject) => (
        <FormField key={orgProject.id} labelText={orgProject.name} inputId={orgProject.id}>
          <BasicSelect
            id={orgProject.id}
            options={roleOptions}
            onSelect={(selected: string[]) => {
              onSelectProjectRole(selected[0], orgProject.id);
            }}
            placeholder="Select a role"
          />
        </FormField>
      ))}
      <br />

      <SectionTitle>JSON output</SectionTitle>
      <CopyToClipboardButton text={JSON.stringify(jsonSrc)} />
      <JsonView src={jsonSrc} />
    </div>
  );
};
